/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import * as Teams from '@microsoft/teams-js';
import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import {
    availableFlowEnvironments,
    flowEnvironmentsLoading,
} from '../../../apollo/reactiveVariables/FlowEnvironmentVariables';
import { Constants } from '../../../common/Constants';
import { Paths } from '../../../common/enums/Paths';
import { useSetListDataMutation } from '../../../data/client/mutations/SetListData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { ListData } from '../../../data/types.generated';
import { OfficeHost } from '../../../hosts/OfficeHost';
import { TeamsHost } from '../../../hosts/TeamsHost';
import { WidgetHost } from '../../../hosts/WidgetHost';
import { EntryPoint, LogCategory, ScenarioName } from '../../../logger';
import { CreateApprovalMobile } from '../../../mobile/containers/CreateApproval/CreateApprovalMobile.component';
import { FileAttachment } from '../../../models/fileService/FileAttachment';
import { DeepLinkType } from '../../../service/DeepLinkManager/IDeepLinkManager';
import { getApprovalFileService } from '../../../service/file-upload/ApprovalsFileService';
import {
    ApprovalCreator,
    ApprovalItem,
    ApprovalProvisioningInfo,
    ApprovalProvisioningState,
    ApprovalResponseType,
    ApprovalStepSettingInput,
    ApprovalPolicyType,
    ApprovalsTextAttachment,
    ApprovalType,
    CreateApprovalClient,
    CreateApprovalControllerInput,
    ListEnvironmentsClient,
    TeamsEnvironment,
} from '../../../service/gen';
import { newGuid } from '../../../utilities/AppUtils';
import { isEntryMenuEnabled, isMultipleAttachmentsEnabled } from '../../../utilities/ECSConfigUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { isDefaultEnvironment, useRequestPageVersion } from '../../../utilities/Utils';
import { PeopleItem, countStepApprovers } from '../../common/StepApproversPicker/PeoplePickerList.component';
import { CreateApprovalComponent, CreateApprovalData } from './CreateApproval.component';

declare const MIDDLE_TIER_ENDPOINT: string;

export interface CreateApprovalContainerProps {
    title?: string;
    // Sets title if user switches between approval type
    setTitle?: (value: string) => void;
    details?: string;
    // Sets details if user switches between approval type
    setDetails?: (value: string) => void;
    // entry point
    entryPoint?: EntryPoint;
    // Should show back button nav bar (applies for mobile personal app)
    showNavigationBar?: boolean;
    // Should show an error message
    showErrorPage?: boolean;
    // Callback on dismissing the modal of this component when specified
    onDismiss?: (listDataOverride?: Partial<ListData>) => void;
    // Set loading page in parent component
    setLoadingPage?: (value: boolean) => void;
    // Set error page in parent component
    setErrorPage?: (value: boolean) => void;
    provisioningInfo?: ApprovalProvisioningInfo;
    // Callback on back button clicked
    onBack?: () => void;
    // The custom footer component which will be rendered in the modal footer
    customFooterComponent?: JSX.Element;
}

export const CreateApprovalContainer: React.FC<CreateApprovalContainerProps> = (
    props: CreateApprovalContainerProps
): JSX.Element => {
    const {
        title,
        setTitle,
        details,
        setDetails,
        entryPoint,
        onDismiss,
        showNavigationBar,
        setLoadingPage,
        setErrorPage,
        showErrorPage,
        provisioningInfo,
        onBack,
        customFooterComponent,
    } = props;

    const host = getAppHost();
    const history = useHistory();
    const logger = host.getLogger();
    const fileUploadAcceptedTypes = Constants.FILE_UPLOAD_ACCEPTED_TYPES;
    const fileService = getApprovalFileService();
    const logCategory = LogCategory.CreateApprovalView;
    const isTeamTagEnabled = host.getEcsConfig().MicrosoftTeamApprovals.TeamTagEnabled;
    const teamId = host.getGroupId();
    const [loading, setLoading] = React.useState(false);
    const requestPageVersion = useRequestPageVersion();
    const isFileUploadEnabled = !(host instanceof WidgetHost);

    const multipleAttachmentsEnabled = isMultipleAttachmentsEnabled();

    // Component Lifecycle
    React.useEffect((): any => {
        // Component mounted
        logger.logTraceInfo(logCategory, 'Rendered Create Approval desktop container');

        return (): void => {
            // Component will unmount
            logger.logTraceInfo(logCategory, 'Closing the Create Approval desktop container');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clientRequestGUID = React.useRef(newGuid());
    let environmentList = useReactiveVar<TeamsEnvironment[]>(availableFlowEnvironments);

    /*
     * Mobile platform has a refresh app feature that allows users to reload the current app view, in that case
     * the environment list needs to be fetched just after each refresh otherwise 'fecth environments' will never
     * be called.
     */
    React.useEffect((): void => {
        if (getAppHost().isMobile() && (!environmentList || environmentList.length === 0)) {
            flowEnvironmentsLoading(true);
            new ListEnvironmentsClient()
                .getTeamsEnvironments(clientRequestGUID.current)
                .then((result: TeamsEnvironment[]): void => {
                    logger.logTraceInfo(logCategory, `Found ${result.length} environments for the user`);
                    availableFlowEnvironments(result);
                    flowEnvironmentsLoading(false);
                })
                .catch((error: any): void => {
                    logger.logTraceHttpException('ListEnvironments', error.message, error.status);
                    logger.logTraceWarning(
                        logCategory,
                        'Failed to fetch environments, will probably only work with default environment now!'
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    environmentList = useReactiveVar<TeamsEnvironment[]>(availableFlowEnvironments);

    /**
     * called when button is pressed
     */
    const onNext = ({
        title: approvalTitle,
        details: approvalDetails,
        approvers,
        allMustRespond,
        isCustomResponsesEnabled,
        environmentTarget,
        customResponses,
        priority,
        files,
        stepApprovers,
    }: CreateApprovalData): void => {
        const clientRequestGUID = newGuid();
        setLoading(true);

        if (setLoadingPage) {
            setLoadingPage(true);
        }

        const titleString = approvalTitle;
        const detailsString = approvalDetails;
        const approversList = approvers;
        const isSequential = stepApprovers?.length > 0 && !approvers.length;
        const approvalResponseType = isSequential
            ? ApprovalResponseType.Sequential
            : isCustomResponsesEnabled
            ? allMustRespond
                ? ApprovalResponseType.CustomResponseAwaitAll
                : ApprovalResponseType.CustomResponse
            : allMustRespond
            ? ApprovalResponseType.BasicAwaitAll
            : ApprovalResponseType.Basic;
        const approvalType = ApprovalType.Basic;

        // remove ResponseOption from api input for non-CustomResponse requests.
        const responseOptionsInput = isCustomResponsesEnabled ? customResponses : undefined;

        logger.logTraceInfo(
            logCategory,
            `Clicked on create approval submit, approval type ${approvalType}, approval response type ${approvalResponseType}`
        );

        // Item link for uploaded file
        const completedFiles =
            files &&
            files.filter(
                (file: FileAttachment): boolean => file.status === UploadStatus.COMPLETE && !!file.shareableUrl
            );

        let item: ApprovalItem = null;
        let textAttachments: ApprovalsTextAttachment[] = null;

        if (!multipleAttachmentsEnabled) {
            item =
                completedFiles && completedFiles.length > 0
                    ? {
                          Link: completedFiles[0].shareableUrl,
                          LinkDescription: completedFiles[0].name,
                      }
                    : null;
        } else {
            textAttachments =
                completedFiles && completedFiles.length > 0
                    ? completedFiles.map((file: FileAttachment): ApprovalsTextAttachment => {
                          const attcahmentItem: ApprovalsTextAttachment = {
                              Subject: file.name,
                              NoteText: file.shareableUrl,
                          };

                          return attcahmentItem;
                      })
                    : null;
        }

        const firstTimeProvisioning =
            provisioningInfo &&
            (provisioningInfo.State === ApprovalProvisioningState.InProgress ||
                provisioningInfo.State === ApprovalProvisioningState.NotProvisioned ||
                provisioningInfo.State === ApprovalProvisioningState.Unknown);

        const approverNamesText = new Array<string>();
        const approversUserObjectId = new Array<string>();

        approversList.forEach((person: IPersonaProps): void => {
            approverNamesText.push(person.text);
        });

        approversList.forEach((person: IPersonaProps): void => {
            approversUserObjectId.push(person.id);
        });

        const getAttachmentsCount = (): number => {
            let count = 0;
            if (item) {
                count = 1;
            } else if (textAttachments) {
                count += textAttachments.length;
            }

            return count;
        };

        const getItemsTypes = (): string => {
            let types: string[] = [];
            if (item && completedFiles[0] && completedFiles[0].type) {
                types = types.concat(completedFiles[0].type);
            } else if (textAttachments && completedFiles) {
                completedFiles.forEach((file: FileAttachment): void => {
                    if (file.type) {
                        types = types.concat(file.type);
                    }
                });
            }

            return types.toString();
        };

        const getItemsSizes = (): string => {
            let sizes: string[] = [];
            if (item && completedFiles[0] && completedFiles[0].size) {
                sizes = sizes.concat(completedFiles[0].size.toString());
            } else if (textAttachments && completedFiles) {
                completedFiles.forEach((file: FileAttachment): void => {
                    if (file.size) {
                        sizes = sizes.concat(file.size?.toString());
                    }
                });
            }

            return sizes.toString();
        };

        const containsAttachments = (): boolean => {
            return !!item || (textAttachments && textAttachments.length > 0);
        };

        logger.logCreateApproval({
            titleSize: approvalTitle.length,
            numberOfApprovers: isSequential ? countStepApprovers(stepApprovers) : approvers.length,
            numberOfSteps: isSequential ? stepApprovers.length : 0,
            detailsSize: approvalDetails.length,
            allMustApprove: allMustRespond ? 'True' : 'False',
            clientRequestId: clientRequestGUID,
            correlationId: clientRequestGUID,
            hasItem: containsAttachments() ? 'True' : 'False',
            isMultipleAttachment: textAttachments && textAttachments.length > 1 ? 'True' : 'False',
            attachmentsCount: getAttachmentsCount(),
            itemsType: getItemsTypes(),
            itemSizes: getItemsSizes(),
            approvalResponseType,
            priority,
            customResponseOptionCount: customResponses ? customResponses.length : undefined,
            customResponseOptionLengths: customResponses
                ? customResponses.map((option: string): number => option.length).join(',')
                : undefined,
            createInDiffEnv: !environmentTarget || !isDefaultEnvironment(environmentTarget, environmentList),
            requestPageVersion,
        });

        // onDismiss and showNavigationBar indicates that the current context is 'personal app'
        if (onDismiss || showNavigationBar) {
            const data: CreateApprovalControllerInput = {
                CallbackUrls: [MIDDLE_TIER_ENDPOINT + Constants.WEBHOOK_ENDPOINT],
                Creator: ApprovalCreator.PersonalApp,
                Source: host.getAppSource(),
                AssignedTo: approversUserObjectId,
                ApproverNames: approverNamesText,
                Type: approvalResponseType,
                ApprovalType: approvalType,
                ClientRequestId: clientRequestGUID,
                Title: titleString.trim(),
                Details: detailsString.trim(),
                Item: multipleAttachmentsEnabled ? null : item,
                TextAttachments: multipleAttachmentsEnabled ? textAttachments : null,
                Priority: priority,
                ResponseOptions: responseOptionsInput,
                FirstTimeProvisioning: firstTimeProvisioning,
                Steps: stepApprovers?.map(
                    (a: PeopleItem): ApprovalStepSettingInput => ({
                        AssignedTo: a.personaList?.map((person: IPersonaProps): string => person.id),
                        ApprovalPolicy: ApprovalPolicyType.Any, // current default is any
                        ApproverNames: a.personaList?.map((person: IPersonaProps): string => person.text),
                    })
                ),
                ...(environmentTarget && { FlowEnvironment: environmentTarget }),
            };
            logger.logTraceInfo(logCategory, 'Calling Approvals create endpoint from personal app');
            new CreateApprovalClient()
                .createApproval(data, clientRequestGUID)
                .then((): void => {
                    logger.findScenario(ScenarioName.CreateApprovals).stop();
                    if (onDismiss) {
                        onDismiss(
                            environmentTarget && {
                                flowEnvironment: environmentTarget,
                            }
                        );
                    } else if (showNavigationBar) {
                        if (
                            getAppHost().getDeepLinkManager().getDeepLinkType() ===
                            DeepLinkType.vivaConnectionsNewApproval
                        ) {
                            history.push(Paths.BasePath + Paths.ApprovalsApp);
                        } else if (isEntryMenuEnabled()) {
                            // when create a basic approval on mobile successfully, go back twice.
                            // because if we go back once we back to mobile create request menu view
                            // we use history.goback here instead of history.push to jump to personal app directly is
                            // because that goback will retain status of personal app like previous
                            // chosen tab(receive/send)
                            history.go(-2);
                        } else {
                            history.goBack();
                        }
                    }
                })
                .catch((error: any): void => {
                    setLoading(false);

                    if (setErrorPage) {
                        setErrorPage(true);
                    }

                    if (setLoadingPage) {
                        setLoadingPage(false);
                    }

                    logger.logTraceHttpException('CreateApproval', error.message, error.status);
                });
        } else {
            logger.logTraceInfo(logCategory, 'Triggering background Approvals creation using bot');

            const botData: CreateApprovalControllerInput = {
                AssignedTo: approversUserObjectId,
                ApproverNames: approverNamesText,
                Type: approvalResponseType,
                ApprovalType: approvalType,
                ClientRequestId: clientRequestGUID,
                Title: titleString.trim(),
                Details: detailsString.trim(),
                Item: multipleAttachmentsEnabled ? null : item,
                TextAttachments: multipleAttachmentsEnabled ? textAttachments : null,
                Priority: priority,
                ResponseOptions: responseOptionsInput,
                FirstTimeProvisioning: firstTimeProvisioning,
                Source: host.getAppSource(),
                Steps: stepApprovers?.map(
                    (a: PeopleItem): ApprovalStepSettingInput => ({
                        AssignedTo: a.personaList?.map((person: IPersonaProps): string => person.id),
                        ApprovalPolicy: ApprovalPolicyType.Any, // current default is any
                        ApproverNames: a.personaList?.map((person: IPersonaProps): string => person.text),
                    })
                ),
            };

            logger.logTraceDebug(logCategory, 'CreateApproval:onNext end for chat/channel');
            logger.flushLogs((): void => Teams.dialog.url.submit(botData));
        }
    };

    const goBack = (): void => {
        logger.logTraceInfo(logCategory, 'Navigating back to the personal app');
        history.goBack();
    };

    const { data: queryData } = useListDataQuery();
    const [setListDataMutation] = useSetListDataMutation();
    const { data: listData } = useListDataQuery();

    const setListData = React.useCallback(
        (lData: Partial<ListData>): void => {
            logger.logTraceInfo(logCategory, 'Refreshing list data');
            setListDataMutation({
                variables: lData,
            });
        },
        [logCategory, logger, setListDataMutation]
    );

    let shouldShowNonDefaultEnvironmentOption = !(host instanceof OfficeHost || host instanceof WidgetHost);
    if (host instanceof TeamsHost) {
        // only display the non-default environment picker in the Teams Personal App context.
        shouldShowNonDefaultEnvironmentOption = host.isGlobalContext();
    }

    return host.isMobile() ? (
        <CreateApprovalMobile
            fileService={fileService}
            onSuccess={goBack}
            onNext={onNext}
            showError={showErrorPage}
            isLoading={loading}
            isFileUploadEnabled={isFileUploadEnabled}
            showNavigationBar={showNavigationBar}
            environmentList={environmentList}
            currentEnvironment={queryData ? queryData.listData.flowEnvironment : null}
            setListData={setListData}
            listData={listData.listData}
            isCreateDiffEnvironmentEnabled={shouldShowNonDefaultEnvironmentOption}
            enableTeamTag={isTeamTagEnabled}
            teamId={teamId}
        />
    ) : (
        <CreateApprovalComponent
            title={title}
            setTitle={setTitle}
            details={details}
            setDetails={setDetails}
            entryPoint={entryPoint}
            fileService={fileService}
            onDismiss={onDismiss}
            onNext={onNext}
            fileUploadAcceptedTypes={fileUploadAcceptedTypes}
            isFileUploadEnabled={isFileUploadEnabled}
            environmentList={environmentList}
            isCreateDiffEnvironmentEnabled={shouldShowNonDefaultEnvironmentOption}
            currentEnvironment={queryData ? queryData.listData.flowEnvironment : null}
            onBack={onBack}
            customFooterComponent={customFooterComponent}
            enableTeamTag={isTeamTagEnabled}
            teamId={teamId}
        />
    );
};
